import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import Grid from "@mui/material/Grid2";
import HoverIconButton from "../../ReusedComponents/HoverIconButton";
import { CircularProgress, Box, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import {
  DataGridPremium,
  GridToolbar,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  useGridApiRef,
  GridToolbarExport,
} from '@mui/x-data-grid-premium';

export default function MaterialUiDataGridPremium(props) {
  const theme = useTheme()
  const { palette = {} } = theme;
  const { primary = {}, success = {} } = palette;
  const classes = {
    container: {
      display: "flex",
      justifyContent: "space-between",
    },
    toolbarDataContainer: {
      display: "flex",
      color: "#6b7078",
    },
    toolbarData: {
      fontSize: "1.15em",
      marginLeft: "15px",
      marginRight: "15px",
      paddingTop: "5px",
    },
  }
  const {
    autoHeight = false,
    checkboxSelectedIds = [],
    checkboxSelection = true,
    columns = [],
    customFilters = null,
    customGridToolbar = null,
    delayedLoad = false,
    disableAggregation = true,
    disableColumnFilter = false,
    disableColumnResize = false,
    disableColumnSelector = false,
    disableCsvExportOption = false,
    disableExcelExportOption = true,
    disableRowGrouping = false,
    disableSelectionOnClick = true,
    disableToolbar = false,

    // Added due to this https://github.com/mui/mui-x/issues/2714
    enableIsRowSelectable = true,
    exportFileName = "",
    exportItemType = "",
    getRowId = () => { },
    getTreeDataPath = () => { },
    handleConfirmationModal = () => { },
    handleExport = false,
    handleRowSelect = () => { },
    height = "80vh",
    hideFooter = false,
    hideFooterRowCount = false,
    loading = false,
    needsCustomToolbar = false,
    onFilterModelChange = () => { },
    onRowClick = () => { },
    onRowOrderChange = () => { },
    onSortModelChange = () => { },
    pageSize = 100,
    pagination = true,
    rowReordering = false,
    rows = [],
    selectionModel = undefined,
    showGridToolBar = false,
    state = {},
    sx = {

      // Makes the background of the table white
      backgroundColor: "white",
      borderColor: "primary.light",
      "& .MuiDataGrid-cell:hover": {
        color: "primary.main",
      },

      // Styles the headers
      "& .MuiDataGrid-columnHeader, .MuiDataGrid-columnHeaders": {
        borderBottom: '0 !important',
        backgroundColor: primary?.main,
        color: "white",
        fontWeight: "bold",
      },

      // Styles the checkbox color
      "& .MuiDataGrid-columnHeader .MuiCheckbox-root": {
        color: "white",
      },

      // Styles the text inside of the header columns
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: "bold",
      },

      '& .MuiDataGrid-sortIcon': {
        color: 'white', // Change color of sort icon
        fontWeight: "bold",
      },
      '& .MuiDataGrid-menuIconButton': {
        color: 'white', // Change color of column menu icon
      },

      // Styles the aggregation label text inside of the header columns
      "& .MuiDataGrid-aggregationColumnHeaderLabel": {
        color: success?.main,
      },

    },
    toolBarButtons = () => { },
    treeData = false,
  } = props;

  //created to remove the actions column from the export
  const csvColumns = columns.reduce((acc, col) => {
    if (col.field.toLowerCase() !== "actions") {
      acc.push(col.field)
    }
    return acc
  }, [])

  const [pgSize, setPgSize] = useState(pageSize);
  const [page, setPage] = useState(0);
  const apiRef = useGridApiRef();
  let components = {};

  // Appends the toolbar
  if (showGridToolBar) {
    // If the parent provides a custom toolbar, we will use that. Otherwise we use the default
    components.Toolbar =
      showGridToolBar && customGridToolbar
        ? customGridToolbar
        : showGridToolBar
          ? GridToolbar
          : null;
  }

  // Appends custom filters
  if (customFilters) {
    components.FilterPanel = customFilters;
  }

  // I need to figure out what was going on here. Should we use mine or his?
  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={classes.container}>
        <Grid sx={classes.toolbarDataContainer}>
          <Grid sx={classes.toolbarData}>
            <GridToolbarColumnsButton
              sx={{ color: primary?.main }}
            />
          </Grid>
          <Grid sx={classes.toolbarData}>
            <GridToolbarFilterButton
              sx={{ color: primary?.main }}
            />
          </Grid>
          <Grid sx={classes.toolbarData}>
            <GridToolbarExport
              csvOptions={{ disableToolbarButton: disableCsvExportOption, fields: csvColumns, fileName: exportFileName }}
              excelOptions={{ disableToolbarButton: disableExcelExportOption, fileName: exportFileName }}
              printOptions={{ disableToolbarButton: true }}
              sx={{ color: primary?.main }}
            />
          </Grid>
          <Grid sx={classes.toolbarData}>
            <GridToolbarQuickFilter debounceMs={500} />
          </Grid>
        </Grid>
        <Grid sx={classes.toolbarDataContainer}>
          {toolBarButtons()}
        </Grid>
        {handleExport ?
          <Grid sx={classes.toolbarDataContainer}>
            <span
            >
              <HoverIconButton
                downloadDocType="Excel"
                exportItemType={exportItemType}
                handleClick={() => { handleExport(state, handleConfirmationModal) }}
                icon={<ContentPasteGoIcon />}
                iconDirection="right"
                text="Export"
              />
            </span>
          </Grid>
          :
          <p>{"  "}</p>}

      </GridToolbarContainer>
    );
  }

  return (
    <Box height={height}>
      <DataGridPremium
        apiRef={apiRef}
        autoHeight={autoHeight}
        checkboxSelection={checkboxSelection}
        columns={columns}
        disableAggregation={disableAggregation}
        disableRowGrouping={disableRowGrouping}
        disableColumnResize={disableColumnResize}
        disableSelectionOnClick={disableSelectionOnClick}
        // following 3 props are used to disable toolbar icons when custom toolbar isnt in use
        disableColumnFilter={disableColumnFilter}
        disableColumnSelector={disableColumnSelector}
        disableDensitySelector
        experimentalFeatures={{ newEditingApi: true }}
        filterMode="client"
        getRowId={useMemo(() => getRowId, [])}
        getTreeDataPath={getTreeDataPath}
        hideFooterRowCount={hideFooterRowCount}
        hideFooter={hideFooter}
        keepNonExistentRowsSelected
        initialState={{
          pinnedColumns: { right: ["actions"] },
          sorting: {
            sortModel: [{
              field: 'timeOfLog', sort: 'desc'
            }]
          },
        }}
        isGroupExpandedByDefault={(node) => {
          //if checkbox selection is not possible, then we dont need to check for expanded nodes
          if (!checkboxSelection) { return false }
          const currNode = apiRef?.current?.getRowNode(node.id);
          let isExpanded = false;
          currNode?.children?.some((child) => {
            if (checkboxSelectedIds.includes(child)) {
              isExpanded = true;
              return true; // exit the loop early
            }
            return false;
          });
          return isExpanded;
        }}
        isRowSelectable={enableIsRowSelectable ? (params) => {
          const currRow = apiRef?.current?.getRowNode(params.id);
          if (currRow?.type === 'group') {
            return false;
          }
          return true;
        } : false}
        loading={loading}
        localeText={{
          noRowsLabel: delayedLoad ? <CircularProgress /> : "No rows",
        }}
        onFilterModelChange={(newFilterModel) => {
          onFilterModelChange(newFilterModel);
        }}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => setPgSize(newPageSize)}
        onRowClick={(rowData) => onRowClick(rowData)}
        onRowOrderChange={(changeInfo) => onRowOrderChange(changeInfo)}
        onRowSelectionModelChange={(selectedRowIds) => {
          handleRowSelect(selectedRowIds);
        }}
        selectionModel={selectionModel}
        onSortModelChange={onSortModelChange}
        page={page}
        pageSize={pgSize}
        pagination={pagination}
        // paginationMode="client"
        // rowCount={rowCountState}
        rowReordering={rowReordering}
        rows={rows}
        rowsPerPageOptions={[10, 25, 50, 100, 250]}
        slots={
          {
            toolbar: disableToolbar ? null : needsCustomToolbar ? CustomToolbar : GridToolbar,
          }
        }
        slotProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: disableCsvExportOption, fields: csvColumns, fileName: exportFileName },
            excelOptions: { disableToolbarButton: disableExcelExportOption, fileName: exportFileName },
            printOptions: { disableToolbarButton: true },
            quickFilterProps: { debounceMs: 500 },
            showQuickFilter: true,
          },
        }}
        sx={sx}
        treeData={treeData}
      />
    </Box>
  );
}
